import {
  faArrowUpArrowDown,
  faChevronRight,
  faCopy,
  faFileImport,
  faMagicWandSparkles,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { KeywordTableState } from '../../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import VoxelMenuDivider from '../../../components/voxel/PopoverMenu/VoxelMenuDivider';
import VoxelMenuHeader from '../../../components/voxel/PopoverMenu/VoxelMenuHeader';
import VoxelMenuItemContainer from '../../../components/voxel/PopoverMenu/VoxelMenuItemContainer';
import VoxelPopoverMenuItem from '../../../components/voxel/PopoverMenu/VoxelPopoverMenuItem';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import GenerateTagGroupDialog from '../TagSection/Dialogs/GenerateTagGroupDialog';
import UploadFromSpreadSheetDialog from '../TagSection/Dialogs/UploadFromSpreadSheetDialog';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import CopyTagGroupsDialog from '../TagSection/Dialogs/CopyTagGroupsDialog';
import VoxelInput from '../../../components/voxel/Forms/VoxelInput';

export interface AddTagGroupButtonProps {
  project: ProjectGq;
  setSelectedTagGroupId?: (id: number) => void;
  reload: () => Promise<unknown>;
}

export const AddTagGroupButton: React.FC<AddTagGroupButtonProps> = ({
  project,
  setSelectedTagGroupId,
  reload,
}) => {
  // Menu
  const [openPopover, setOpenPopover] = useState(false);

  // Create
  const createTagGroupMutation = projectsApiService.mutations.tags.useCreateTagGroup();
  const [createLoading, setCreateLoading] = useState(false);

  const handleCreateTagGroup = () => {
    setCreateLoading(true);
    createTagGroupMutation({ projectId: project.id, name: '' })
      .then((result) =>
        reload().then(() => {
          setSelectedTagGroupId(result.id);
          setEditTagGroupId(result.id);
          setEditTagGroupName(result.name);
        })
      )
      .finally(() => {
        setCreateLoading(false);
      });
  };

  // Edit tag group name
  const updateTagGroupMutation = projectsApiService.mutations.tags.useEditTagGroupName();
  const [editTagGroupId, setEditTagGroupId] = useState<number>(undefined);
  const [editTagGroupName, setEditTagGroupName] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (!openPopover) {
      setEditTagGroupId(undefined);
      setEditTagGroupName('');
    }
  }, [openPopover]);

  const handleEditName = () => {
    setEditLoading(true);
    updateTagGroupMutation({
      name: editTagGroupName,
      id: editTagGroupId,
      projectId: project.id,
    }).finally(() => {
      setEditLoading(false);
      setEditTagGroupId(undefined);
      setOpenPopover(false);
    });
  };

  // Generate
  const [showGenerateTagGroup, setShowGenerateTagGroup] = useState(false);
  const [openImportKeywordTagsDialog, setOpenImportKeywordTagsDialog] = useState(false);
  const [openCopyGroupsDialog, setOpenCopyGroupsDialog] = useState(false);

  return (
    <>
      <GenerateTagGroupDialog
        reload={reload}
        project={project}
        openDialog={showGenerateTagGroup}
        setOpenDialog={setShowGenerateTagGroup}
        showTagGroup={(tagGroupId) => {
          setSelectedTagGroupId(tagGroupId);
        }}
      />
      <CopyTagGroupsDialog
        project={project}
        openDialog={openCopyGroupsDialog}
        setOpenDialog={setOpenCopyGroupsDialog}
        reload={reload}
      />
      <UploadFromSpreadSheetDialog
        project={project}
        openDialog={openImportKeywordTagsDialog}
        setOpenDialog={setOpenImportKeywordTagsDialog}
        onSuccess={() => reload().then(() => setOpenImportKeywordTagsDialog(null))}
      />
      <VoxelPopover open={openPopover} onOpenChange={setOpenPopover}>
        <VoxelPopoverTrigger enableActive>
          <VoxelButton
            title="Add tag group"
            size="sm"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            color={!!project.tagGroups.length ? 'gray' : 'success'}
          />
        </VoxelPopoverTrigger>
        <VoxelPopoverContent>
          {!editTagGroupId && (
            <VoxelMenuItemContainer size="md">
              <VoxelPopoverMenuItem
                title="New Empty Tag Group"
                icon={<FontAwesomeIcon icon={faPlus} />}
                submitting={createLoading}
                closePopoverOnClick={false}
                onClick={handleCreateTagGroup}
                disabled={createLoading}
              />
              <VoxelMenuDivider />
              <VoxelPopoverMenuItem
                title="New Generated Tag Group"
                icon={<FontAwesomeIcon icon={faMagicWandSparkles} />}
                onClick={() => setShowGenerateTagGroup(true)}
                disabled={createLoading}
              />
              <VoxelPopoverMenuItem
                title={'Copy From Project'}
                onClick={() => setOpenCopyGroupsDialog(true)}
                disabled={createLoading}
                icon={<FontAwesomeIcon icon={faCopy} />}
              />
              <VoxelPopoverMenuItem
                title={'Upload From Spreadsheet (Beta)'}
                onClick={() => setOpenImportKeywordTagsDialog(true)}
                disabled={createLoading}
                icon={<FontAwesomeIcon icon={faFileImport} />}
              />
            </VoxelMenuItemContainer>
          )}
          {editTagGroupId && (
            <form
              className="p-4 flex flex-row items-end gap-2"
              onSubmit={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <VoxelInput
                label="Tag group name"
                labelPosition="top"
                value={editTagGroupName}
                autoFocus
                onChange={(e) => setEditTagGroupName(e.target.value)}
                disabled={editLoading}
                placeholder="Products..."
              />
              <VoxelButton
                title="Continue"
                color="primary"
                endIcon={<FontAwesomeIcon icon={faChevronRight} />}
                onClick={handleEditName}
                submitting={editLoading}
                type="submit"
              />
            </form>
          )}
        </VoxelPopoverContent>
      </VoxelPopover>
    </>
  );
};

export default AddTagGroupButton;
